// What promo publishes to the One Retail shell once embedded: navbar items and indicators,
// recent/frequent entries for the shell home page, and the browser URL. Language flows the other way, from the
// shell. All of it is a no-op standalone, where `shellBridge()` is null.

import { find, get } from 'lodash';
import i18n from '@/js/vue-i18n';
import store from '@/js/store';
import { toSentenceCase } from '@/js/utils/string-utils';
import { fullDateText, weekNumberClient } from '@/js/filters/date-filters';
import { APP_KEY, PLATFORM_BRIDGE_VERSION, shellBridge } from './platform-bridge';
import { resolveLocale } from './resolve-locale';
import { watchAndReportCurrentRoute } from './recently-visited';
import { watchAndReportTabTitle } from './tab-title';

// Shell navbar items from promo's route table - ADR-007 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/007-navigation-model-and-contribution.md
const buildMainNavbarItems = router => {
    const { toggleLogic, mainNavbarRoutes } = store.state.clientConfig;
    // The same toggle promo's route guard and `feature-toggle` components read.
    const permitted = route => {
        const toggle = get(route, 'meta.canAccess');
        return toggle ? !!toggleLogic[toggle] : true;
    };
    const visible = route => !route.omitFromNav && !!route.linkText && permitted(route);

    const item = (route, order) => ({
        id: route.id || route.name || route.path,
        label: toSentenceCase(i18n.global.t(route.linkText)),
        to: route.path,
        order,
    });

    // Same tiebreak as main-navbar.vue's `routes`, so the shell shows promo's own order. `order` is
    // sequential, not the raw position: the shell sorts unpositioned routes to the top.
    const items = router.options.routes
        .filter(visible)
        .map(route => ({ route, index: get(mainNavbarRoutes, [route.id, 'position']) }))
        .sort((a, b) => {
            if (Number.isFinite(a.index) && Number.isFinite(b.index)) return a.index - b.index;
            if (Number.isFinite(a.index)) return -1;
            if (Number.isFinite(b.index)) return 1;
            return (b.route.name || '').localeCompare(a.route.name || '');
        })
        .map(({ route }, i) => item(route, i));

    // Admin is `omitFromNav` because standalone it is a cog on the navbar, which is hidden embedded.
    // Contribute it as a trailing item, on the toggle that gates the cog: embedding must not change
    // who gets in.
    const adminRoute = find(router.options.routes, { path: '/admin' });
    if (adminRoute && permitted(adminRoute)) {
        items.push({ ...item(adminRoute, items.length), icon: 'mdi-cog' });
    }

    return items;
};

// Feed the shell home page's recent/frequent lists from the route's `recordRecentlyOpenedInShell`
// metadata. Preparation and planning share the sub-campaign key, so the two views collapse into one
// entry that resumes wherever the user was last.
const installRecents = router =>
    watchAndReportCurrentRoute(
        () => router.currentRoute.value,
        entry => shellBridge()?.recents.record(APP_KEY, entry)
    );

// The shell owns the document, so it writes the tab; promo reports the page it is showing
// - ADR-018 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/018-shell-owns-the-tab-title.md
// Standalone promo has no tab title of its own, so this only exists embedded.
const installTabTitle = router =>
    watchAndReportTabTitle(
        () => router.currentRoute.value,
        detail => shellBridge()?.tabTitle.set(APP_KEY, detail)
    );

// Gated on a profile: logged out there are no translations or toggles to build labels from, so
// promo contributes nothing.
const installNavbarItems = router => {
    const syncNavbarItems = () => {
        const bridge = shellBridge();
        if (!bridge) return;
        if (!store.state.context.profile) {
            bridge.navbar.unregister(APP_KEY);
            return;
        }
        bridge.navbar.register(APP_KEY, buildMainNavbarItems(router));
    };

    const unwatch = store.watch(
        state => [
            state.context.profile,
            state.clientConfig.toggleLogic,
            state.clientConfig.mainNavbarRoutes,
        ],
        syncNavbarItems,
        { deep: true }
    );
    syncNavbarItems();
    return unwatch;
};

// The current week, which standalone promo shows on its own navbar. Nothing until the week
// calendar has loaded, and nothing for reporting-only clients, which hide it standalone too.
const buildNavbarIndicators = () => {
    const { moment } = store.state;
    if (!moment || get(store.state, 'clientConfig.toggleLogic.reportingOnly', false)) return [];
    const date = new Date();
    const weekOfYear = get(store.getters['weeks/getWeekByDate'](date), 'weekOfYear');
    if (weekOfYear == null) return [];
    return [
        {
            id: 'current-week',
            emphasis: weekNumberClient(moment, weekOfYear),
            label: fullDateText(moment, date),
        },
    ];
};

// Installed after `followShellLocale`, so a locale change has been applied by the time this
// subscriber rebuilds the labels.
const installNavbarIndicators = () => {
    const bridge = shellBridge();
    if (!bridge) return () => {};

    const syncNavbarIndicators = () =>
        bridge.navbar.setIndicators(APP_KEY, buildNavbarIndicators());

    const unwatch = store.watch(
        state => [
            state.moment,
            state.weeks.weeks,
            state.clientConfig.toggleLogic,
            state.clientConfig.i18nTranslations,
        ],
        syncNavbarIndicators
    );
    const unsubscribe = bridge.subscribe('localeChanged', syncNavbarIndicators);
    syncNavbarIndicators();

    return () => {
        unwatch();
        unsubscribe();
    };
};

// Let the shell navigate using promo's router, and report back to shell the current promo route.
// Promo navigates on a memory history, so every route change has to be reported for the shell to write it
// into the URL.
// The report during install replaces: the shell already pushed the entry that opened us.
const installAppRouter = router => {
    shellBridge()?.appRouter.register(APP_KEY, to => router.push(to));

    const reportCurrentRoute = replace =>
        shellBridge()?.appRouter.reportCurrentRoute(APP_KEY, router.currentRoute.value.fullPath, {
            replace,
        });
    router.afterEach(() => reportCurrentRoute(false));
    reportCurrentRoute(true);
};

// Embedded, the shell picks promo's language; the client config's `defaultLocale` is only a
// starting point. Two traps: `vue-i18n.js` exports a spread of the instance, so `i18n.locale` is a
// dead property on a copy and the write has to go through `i18n.global`; and `global.locale` is a
// plain string, not a ref, because `createI18n` runs without `legacy: false`.
const followShellLocale = () => {
    const bridge = shellBridge();
    if (!bridge) return () => {};

    // `subscribe` only fires on change, and the shell restores its stored locale long before promo
    // mounts, so the current value has to be read explicitly as well.
    let requested = bridge.locale;

    const applyShellLocale = () => {
        const locale = resolveLocale(requested, i18n.global.availableLocales);
        if (!locale || locale === i18n.global.locale) return;
        i18n.global.locale = locale;
    };

    const unsubscribe = bridge.subscribe('localeChanged', tag => {
        requested = tag;
        applyShellLocale();
    });
    // The catalogues load after login, so the list to resolve against grows and has to be re-read.
    const unwatch = store.watch(state => state.clientConfig.i18nTranslations, applyShellLocale);
    applyShellLocale();

    return () => {
        unsubscribe();
        unwatch();
    };
};

// Teardown handles for what outlives promo's app instance: `app.unmount()` takes neither the
// module-singleton Vuex store nor the shell's stores with it. The router's `afterEach` hooks need
// no handle; that router is created per app.
let teardown = [];

// Call once when embedded, after mount and after the landing navigation.
export default function installShellContributions(router) {
    // Lets the shell see which promo builds are ahead of it - ADR-019 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/019-bridge-versioning.md
    shellBridge()?.reportBridgeVersion(APP_KEY, PLATFORM_BRIDGE_VERSION);

    const stopNavbarItems = installNavbarItems(router);
    installAppRouter(router);
    const stopRecents = installRecents(router);
    teardown = [
        stopNavbarItems,
        stopRecents,
        installTabTitle(router),
        followShellLocale(),
        installNavbarIndicators(),
    ];
}

// Withdraw everything above. Without it promo's nav entry stays on the shell navbar and a language
// switch made in the next product still drives promo's i18n.
export function uninstallShellContributions() {
    teardown.forEach(stop => stop?.());
    teardown = [];
    shellBridge()?.navbar.unregister(APP_KEY);
    shellBridge()?.navbar.setIndicators(APP_KEY, null);
    shellBridge()?.appRouter.unregister(APP_KEY);
    shellBridge()?.tabTitle.set(APP_KEY, null);
}
